'use client';

/**
 * MastheadAddButton — the 34×34 ink-filled "+" square that sits in
 * the top-right corner of every list-page masthead: /yarn,
 * /inventory, /techniques, /inspiration. Opens the "create new X"
 * form for that page.
 *
 * Recipe (canonical, identical across the four sites it replaced
 * in build 1370):
 *   width: 34, height: 34 (fixed square)
 *   background: var(--ink), color: var(--paper-1)
 *   border: none, border-radius: 0 (B1 zine — square corners)
 *   font-family: geist sans
 *   font-weight: 900 (heavier than ZineButton's 800 — the glyph
 *                    needs the extra mass at this small size)
 *   font-size: 22
 *   line-height: 1
 *
 * Distinct from <ZineButton icon> which is 42×42 with font-weight 800.
 * Different role (prominent page-level CTA vs row-level icon button),
 * different visual weight.
 *
 * Always renders a real `<button type="button">`. The glyph is
 * hard-baked into the children to keep call sites uniform — pass
 * an explicit `children` if you want something other than `+`.
 *
 * Default aria-label is "Add"; override for accessibility when the
 * action is more specific ("Add yarn", "Add needle", etc.).
 */

import type { CSSProperties, MouseEventHandler, ReactNode } from 'react';

export type MastheadAddButtonProps = {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /** The glyph shown inside the square. Defaults to "+". */
  children?: ReactNode;
  /** Accessible label. Defaults to "Add". Override when more specific is meaningful. */
  'aria-label'?: string;
  title?: string;
  disabled?: boolean;
  style?: CSSProperties;
  className?: string;
};

export function MastheadAddButton({
  onClick,
  children = '+',
  'aria-label': ariaLabel = 'Add',
  title,
  disabled,
  style,
  className,
}: MastheadAddButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      title={title}
      className={className}
      style={{
        flexShrink: 0,
        background: 'var(--ink)',
        color: 'var(--paper-1)',
        border: 'none',
        width: 34,
        height: 34,
        fontFamily: 'var(--font-geist-sans), system-ui, sans-serif',
        fontWeight: 900,
        fontSize: 22,
        lineHeight: 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        borderRadius: 0,
        ...style,
      }}
    >
      {children}
    </button>
  );
}
